import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { LOGO_BASE64, BRAND_TEXT_BASE64 } from "../utils/brandAssets";
import { DASHBOARD_URL } from "../utils/domain";
import { featureHighlights, featurePages } from "../utils/featureCatalog";
import { featureScreenshotSources } from "../utils/featureMedia";
import "../styles/FeatureLanding.css";

export default function FeatureLandingPage({ user }) {
  const { featureSlug } = useParams();
  const feature = featurePages[featureSlug];

  useEffect(() => {
    if (feature) {
      document.title = `LinkSquire - ${feature.title}`;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [feature]);

  if (!feature) {
    return <Navigate to="/" replace />;
  }

  const FeatureIcon = feature.icon;
  const screenshotSource = feature.screenshot?.src || featureScreenshotSources[featureSlug];

  return (
    <div className="feature-page">
      <div className="feature-bg feature-bg-one" aria-hidden="true" />
      <div className="feature-bg feature-bg-two" aria-hidden="true" />

      <nav className="feature-navbar">
        <div className="feature-navbar-left">
          <Link to="/" className="feature-brand" draggable="false">
            <img src={LOGO_BASE64} alt="" className="feature-brand-logo" draggable="false" />
            <img src={BRAND_TEXT_BASE64} alt="" className="feature-brand-text" draggable="false" />
          </Link>
          <Link to="/" className="feature-back-link">
            <FiArrowLeft size={14} /> Back to home
          </Link>
        </div>
        <div className="feature-navbar-right">
          {!user && (
            <a href={DASHBOARD_URL} className="feature-signin-btn">
              Sign In
            </a>
          )}
          <a href={DASHBOARD_URL} className="btn-primary feature-cta-btn">
            {user ? "Open Dashboard" : "Start Free"} <FiArrowRight size={14} />
          </a>
        </div>
      </nav>

      <header className="feature-hero">
        <div className="feature-hero-grid">
          <div className="feature-hero-copy">
            <span className="feature-eyebrow">{feature.eyebrow}</span>
            <h1>{feature.title}</h1>
            <p>{feature.description}</p>
            <div className="feature-hero-actions">
              <a href={DASHBOARD_URL} className="btn-primary feature-hero-btn">
                Get Started in LinkSquire <FiArrowRight size={15} />
              </a>
              <Link to="/" className="btn-secondary feature-secondary-btn">
                Compare all features
              </Link>
            </div>
          </div>

          <aside className="feature-preview">
            <div className="feature-screenshot-placeholder" aria-label={feature.screenshot.label}>
              {screenshotSource ? (
                <img
                  src={screenshotSource}
                  alt={`${feature.title} promotional screenshot`}
                  className="feature-screenshot-image"
                  loading="lazy"
                />
              ) : (
                <>
                  <FeatureIcon size={18} />
                  <strong>Screenshot placeholder</strong>
                  <span>
                    {feature.screenshot.width} x {feature.screenshot.height}
                  </span>
                  <p>{feature.screenshot.label}</p>
                </>
              )}
            </div>
            <div className="feature-screenshot-caption">
              <span>
                {feature.screenshot.width} x {feature.screenshot.height}
              </span>
              <p>{feature.screenshot.label}</p>
            </div>
            <div className="feature-stat-grid">
              {feature.heroStats.map((stat) => (
                <div key={stat.label} className="feature-stat-card">
                  <h4>{stat.value}</h4>
                  <p>{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="feature-preview-title">
              <FeatureIcon size={17} width={17} height={17} aria-hidden="true" focusable="false" /> Feature Snapshot
            </div>
            <div className="feature-highlight-list">
              {featureHighlights.map(({ icon: Icon, text }) => (
                <span key={text}>
                  <Icon size={13} /> {text}
                </span>
              ))}
            </div>
          </aside>
        </div>
      </header>

      <section className="feature-section">
        <h2>Core capabilities</h2>
        <p className="feature-subtitle">
          This feature is built to give teams practical control and measurable
          performance, not just another settings screen.
        </p>
        <div className="feature-capability-grid">
          {feature.capabilities.map((capability) => (
            <article key={capability.title} className="feature-capability-card">
              <h3>{capability.title}</h3>
              <p>{capability.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="feature-section feature-section-tight">
        <div className="feature-outcomes-wrap">
          <div>
            <h2>Business outcomes</h2>
            <p className="feature-subtitle">
              Teams choose this capability because it improves execution speed
              and campaign quality in day-to-day operations.
            </p>
          </div>
          <ul className="feature-outcome-list">
            {feature.outcomes.map((outcome) => (
              <li key={outcome}>{outcome}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="feature-section feature-section-tight">
        <h2>Where this feature shines</h2>
        <div className="feature-usecase-grid">
          {feature.useCases.map((useCase) => (
            <article key={useCase.heading} className="feature-usecase-card">
              <h3>{useCase.heading}</h3>
              <p>{useCase.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="feature-bottom-cta">
        <h2>Ready to put this to work?</h2>
        <p>
          Start in LinkSquire today and combine this capability with the full
          link management and analytics platform.
        </p>
        <div className="feature-hero-actions">
          <a href={DASHBOARD_URL} className="btn-primary feature-hero-btn">
            Launch Dashboard <FiArrowRight size={15} />
          </a>
          <Link to="/" className="btn-secondary feature-secondary-btn">
            Explore all features
          </Link>
        </div>
      </section>
    </div>
  );
}
